import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { langFlag } from '../lib/languageFlags.js'

const UI_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'mn', name: 'Монгол' },
  { code: 'ko', name: '한국어' },
  { code: 'ja', name: '日本語' },
  { code: 'es', name: 'Español' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'id', name: 'Bahasa' },
] as const

interface LanguagePickerProps {
  variant: 'inline' | 'compact'
  /** Where the popover opens relative to the button */
  position?: 'above' | 'below' | 'right'
}

export default function LanguagePicker({ variant, position }: LanguagePickerProps) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const current = UI_LANGUAGES.find(l => l.code === i18n.language) ?? UI_LANGUAGES[0]

  const switchLang = (code: string) => {
    void i18n.changeLanguage(code)
    localStorage.setItem('silkvoice-lang', code)
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {variant === 'inline' ? (
        <button className="lang-picker-btn" onClick={() => setOpen(o => !o)}>
          <span>{langFlag(current.code)}</span>
          <span>{current.code.toUpperCase()}</span>
          <ChevronIcon open={open} />
        </button>
      ) : (
        <button className="lang-picker-btn compact" onClick={() => setOpen(o => !o)} aria-label="Language">
          <LangGlobeIcon />
        </button>
      )}

      {open && (
        <div className={`lang-picker-menu${position === 'right' ? ' from-sidebar' : position === 'below' ? ' from-navbar' : ''}`}>
          {UI_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className={`lang-picker-item${lang.code === i18n.language ? ' active' : ''}`}
              onClick={() => switchLang(lang.code)}
            >
              <span className="lang-picker-flag">{langFlag(lang.code)}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

function LangGlobeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
    </svg>
  )
}
